/**
 * Champ de directions — logique pure (Boîte à outils, Tle → prépa).
 *
 * En chaque point (x ; y) du plan, un petit segment de pente y′ donnée par
 * l'équation différentielle : c'est la tangente de la solution qui passe par
 * ce point. Par chaque point passe une et une seule solution : la condition
 * initiale y(x₀) = y₀ fixe la constante. Deux familles du programme de Tle :
 *
 * - y′ = ay + b (a ≠ 0) : solutions y = Ce^(ax) + m, où m = −b/a est la
 *   solution constante (là où les segments sont horizontaux) ;
 * - y′ = f(x) : solutions y = F(x) + C, F primitive de f (translations
 *   verticales les unes des autres).
 *
 * a est pris dans {±0,5 ; ±1 ; ±2} et b par demis, de sorte que m reste dans
 * la fenêtre : les constantes m et y₀ − m sont alors des décimaux exacts.
 * Les écritures avec exposant sont des « morceaux » (`Seg`) que l'outil met en
 * forme (exposant en <sup>) ; `plainText` les aplatit (tests, lecteur d'écran).
 */

import { exact, frac, fromDecimal, mul, sub, value, type Frac } from '../../ui/exact';
import { formatNumber } from '../../ui/format';
import type { Pt, Viewport } from '../../ui/frame';

export const WINDOW: Viewport = { xMin: -4, xMax: 4, yMin: -4, yMax: 4 };
export const A_VALUES = [-2, -1, -0.5, 0.5, 1, 2] as const;
/** b par demis, |b| ≤ bMax(a) : la solution constante m = −b/a reste dans [−3,5 ; 3,5] */
export const bMax = (a: number) => Math.floor(Math.min(4, 3.5 * Math.abs(a)) * 2) / 2;

/** un morceau d'écriture : du texte, ou un exposant */
export type Seg = string | { sup: string };

export type PrimId = 'lin' | 'cubic' | 'cos' | 'exp';

export interface Prim {
  id: PrimId;
  /** f(x), pour l'équation y′ = f(x) */
  fText: string;
  /** F(x), une primitive (avec exposant pour eˣ) */
  F: Seg[];
  /** f lu par le lecteur d'écran */
  spoken: string;
  f: (x: number) => number;
  Fn: (x: number) => number;
  /** F(x₀) exacte quand on sait l'écrire, sinon null */
  FExact: (x0: number) => Frac | null;
  /** « sin 1 », « e^(0,5) » : F(x₀) écrite, quand elle n'est pas exacte */
  FAt?: (x0: number) => Seg[];
}

export const PRIMS: readonly Prim[] = [
  {
    id: 'lin',
    fText: '2x',
    F: ['x²'],
    spoken: '2 x',
    f: (x) => 2 * x,
    Fn: (x) => x * x,
    FExact: (x0) => {
      const X = fromDecimal(x0);
      return mul(X, X);
    },
  },
  {
    id: 'cubic',
    fText: 'x² − 1',
    F: ['x³/3 − x'],
    spoken: 'x au carré moins 1',
    f: (x) => x * x - 1,
    Fn: (x) => (x * x * x) / 3 - x,
    FExact: (x0) => {
      const X = fromDecimal(x0);
      return sub(mul(mul(mul(X, X), X), frac(1, 3)), X);
    },
  },
  {
    id: 'cos',
    fText: 'cos x',
    F: ['sin x'],
    spoken: 'cosinus de x',
    f: Math.cos,
    Fn: Math.sin,
    FExact: (x0) => (x0 === 0 ? frac(0) : null),
    FAt: (x0) => [x0 < 0 ? `sin(${num(x0)})` : `sin ${num(x0)}`],
  },
  {
    id: 'exp',
    fText: 'eˣ',
    F: ['e', { sup: 'x' }],
    spoken: 'exponentielle de x',
    f: Math.exp,
    Fn: Math.exp,
    FExact: (x0) => (x0 === 0 ? frac(1) : null),
    FAt: (x0) => (x0 === 1 ? ['e'] : ['e', { sup: num(x0) }]),
  },
];

export const primById = (id: PrimId) => PRIMS.find((p) => p.id === id) ?? PRIMS[0]!;

export type Eq = { kind: 'affine'; a: number; b: number } | { kind: 'prim'; id: PrimId };

/* ---------- écritures ---------- */

/** « 0,5 », « −2 », « 1,25 » */
export function num(v: number, digits = 6): string {
  const r = Number(v.toFixed(10));
  return formatNumber(Object.is(r, -0) ? 0 : r, digits).replace('-', '−');
}

/** valeur approchée à 3 chiffres significatifs, milliers séparés (« 0,000912 », « −7 680 ») */
export function approx(v: number): string {
  const r = Number(v.toPrecision(3));
  const t = num(r, 10);
  const [int, dec] = t.split(',');
  const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, String.fromCharCode(0x202f));
  return dec ? `${grouped},${dec}` : grouped;
}

/**
 * Texte simple. `caret` : « e^(−x) » (tests) ; `spoken` : « e exposant (−x) »
 * (lecteur d'écran).
 */
export function plainText(segs: readonly Seg[], mode: 'caret' | 'spoken' = 'caret'): string {
  let out = '';
  for (const s of segs) {
    if (typeof s === 'string') out += s;
    else out += mode === 'caret' ? `^(${s.sup})` : ` exposant (${s.sup})`;
  }
  return out;
}

/** « −0,5y », « y », « −y », « 2y » */
function coefText(a: number, v: string): string {
  if (a === 1) return v;
  if (a === -1) return `−${v}`;
  return `${num(a)}${v}`;
}

/** « + 2 », « − 1,5 », « » */
export function addend(v: number): string {
  if (v === 0) return '';
  return v > 0 ? ` + ${num(v)}` : ` − ${num(-v)}`;
}

/** « (−2) » : un nombre négatif après un signe moins */
const paren = (t: string) => (t.startsWith('−') ? `(${t})` : t);

/** « y′ = −0,5y + 1 » */
export function eqText(eq: Eq): string {
  if (eq.kind === 'prim') return `y′ = ${primById(eq.id).fText}`;
  return `y′ = ${coefText(eq.a, 'y')}${addend(eq.b)}`;
}

/** l'exposant a(x − x₀) : « −0,5x », « x − 1 », « 2(x + 1,5) », « −(x − 1) » */
export function expArg(a: number, x0: number): string {
  if (x0 === 0) return coefText(a, 'x');
  const inner = `x${addend(-x0)}`;
  if (a === 1) return inner;
  if (a === -1) return `−(${inner})`;
  return `${num(a)}(${inner})`;
}

/** « 3e », « −e », « e », « 0,5e » */
function coefE(k: number): string {
  if (k === 1) return 'e';
  if (k === -1) return '−e';
  return `${num(k)}e`;
}

/** k·e^t écrit : « 3e^(0,5) », « −e », « 2 » */
function kExp(k: number, t: number): Seg[] {
  if (t === 0) return [num(k)];
  if (t === 1) return [coefE(k)];
  return [coefE(k), { sup: num(t) }];
}

/** e^t écrit : « e », « e^(−0,5) » */
const eSegs = (t: number): Seg[] => (t === 1 ? ['e'] : ['e', { sup: num(t) }]);

/** solutions générales : « y = Ce^(−0,5x) + 2 », « y = x² + C » */
export function generalSegs(eq: Eq): Seg[] {
  if (eq.kind === 'prim') return ['y = ', ...primById(eq.id).F, ' + C'];
  const m = -eq.b / eq.a;
  return ['y = Ce', { sup: expArg(eq.a, 0) }, addend(m)];
}

export interface Solution {
  /** la fonction solution */
  y: (x: number) => number;
  /** « y = 3e^(−0,5(x − 1)) + 2 » */
  segs: Seg[];
  /** comment la condition initiale fixe la constante */
  how: Seg[];
  /** solution constante */
  constant: boolean;
}

/** la solution qui vérifie y(x₀) = y₀ (p sur la grille des demis) */
export function solutionThrough(eq: Eq, p: Pt): Solution {
  const { x: x0, y: y0 } = p;
  if (eq.kind === 'affine') {
    const { a, b } = eq;
    const m = -b / a;
    const K = y0 - m;
    const y = (x: number) => K * Math.exp(a * (x - x0)) + m;
    if (Math.abs(K) < 1e-12)
      return {
        y,
        segs: [`y = ${num(m)}`],
        how: [`y₀ = −b/a = ${num(m)} : c’est la solution constante.`],
        constant: true,
      };
    const segs: Seg[] = [`y = ${coefE(K)}`, { sup: expArg(a, x0) }, addend(m)];
    const shift = m === 0 ? num(y0) : `(${num(y0)} − ${paren(num(m))})`;
    if (x0 === 0)
      return {
        y,
        segs,
        how: [
          `y(0) = C${addend(m)} = ${num(y0)}, donc C = ${m === 0 ? '' : `${shift} = `}${num(K)}.`,
        ],
        constant: false,
      };
    // y(x₀) = Ce^(a x₀) + m = y₀, donc C = (y₀ − m)e^(−a x₀)
    const C = K * Math.exp(-a * x0);
    return {
      y,
      segs,
      how: [
        `y(${num(x0)}) = C`,
        ...eSegs(a * x0),
        `${addend(m)} = ${num(y0)}, donc C = `,
        ...(m === 0 ? [] : [shift, ...eSegs(-a * x0), ' = ']),
        ...kExp(K, -a * x0),
        ` ≈ ${approx(C)}.`,
      ],
      constant: false,
    };
  }
  const pr = primById(eq.id);
  const Fx0 = pr.FExact(x0);
  const yOf = (C: number) => (x: number) => pr.Fn(x) + C;
  const intro: Seg[] = ['F(x) = ', ...pr.F, ' (une primitive de f) ; '];
  if (Fx0) {
    const C = sub(fromDecimal(y0), Fx0);
    const Ct = exact(C).plain;
    return {
      y: yOf(value(C)),
      segs: ['y = ', ...pr.F, C.p === 0 ? '' : C.p > 0 ? ` + ${Ct}` : ` − ${Ct.slice(1)}`],
      how: [...intro, `C = y₀ − F(x₀) = ${num(y0)} − ${paren(exact(Fx0).plain)} = ${Ct}.`],
      constant: false,
    };
  }
  const C = y0 - pr.Fn(x0);
  return {
    y: yOf(C),
    segs: ['y = ', ...pr.F, y0 === 0 ? '' : addend(y0), ' − ', ...(pr.FAt?.(x0) ?? [])],
    how: [...intro, `C = y₀ − F(x₀) = ${num(y0)} − `, ...(pr.FAt?.(x0) ?? []), ` ≈ ${approx(C)}.`],
    constant: false,
  };
}

/** pente du champ en (x ; y) */
export function slope(eq: Eq, x: number, y: number): number {
  return eq.kind === 'affine' ? eq.a * y + eq.b : primById(eq.id).f(x);
}

/** points du champ (pas d'un demi), sans les bords */
export function fieldNodes(vp: Viewport = WINDOW, step = 0.5): Pt[] {
  const out: Pt[] = [];
  for (let x = vp.xMin + step; x < vp.xMax - 1e-9; x += step)
    for (let y = vp.yMin + step; y < vp.yMax - 1e-9; y += step)
      out.push({ x: Number(x.toFixed(6)), y: Number(y.toFixed(6)) });
  return out;
}

/** comportement des solutions de y′ = ay + b, en une phrase */
export function behaviour(a: number, b: number): Seg[] {
  const m = num(-b / a);
  return a < 0
    ? [
        `a < 0 : toutes les solutions tendent vers ${m} quand x → +∞ (Ce`,
        { sup: 'ax' },
        ` → 0) : leurs courbes se rapprochent de la droite y = ${m}.`,
      ]
    : [
        `a > 0 : hormis la solution constante y = ${m}, les solutions s’écartent de cette droite quand x → +∞, vers +∞ si elles partent au-dessus, vers −∞ en dessous.`,
      ];
}

/* ---------- défi ---------- */

export type QuizKind = 'equation' | 'solution' | 'primitive' | 'solution-x0';

/** le type de chaque question, dans l'ordre des 5 manches */
export const QUIZ_KINDS: readonly QuizKind[] = [
  'equation',
  'solution',
  'primitive',
  'solution-x0',
  'equation',
];

export interface QuizOption {
  segs: Seg[];
  correct: boolean;
  /** pourquoi cette réponse est fausse (avec les valeurs de la question) */
  hint?: Seg[];
  /** la fonction proposée (pour la tracer), sauf pour « quelle équation ? » */
  y?: (x: number) => number;
}

export interface Quiz {
  kind: QuizKind;
  eq: Eq;
  /** condition initiale (défis « solution » et « primitive ») */
  x0: number;
  y0: number;
  options: QuizOption[];
  /** explication de la bonne réponse */
  explain: Seg[];
}

const pick = <T>(xs: readonly T[], rnd: () => number): T => xs[Math.floor(rnd() * xs.length)]!;

function shuffle<T>(xs: T[], rnd: () => number): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

const sameEq = (e: Eq, prev?: Quiz) => !!prev && JSON.stringify(e) === JSON.stringify(prev.eq);

function pickAB(
  rnd: () => number,
  prev: Quiz | undefined,
  as: readonly number[] = A_VALUES,
): { a: number; b: number } {
  for (let k = 0; k < 80; k++) {
    const a = pick(as, rnd);
    const n = bMax(a) * 2;
    const b = (Math.floor(rnd() * (2 * n + 1)) - n) / 2;
    // m = −b/a lisible : un multiple de 0,5 non nul, |m| ≤ 3
    const m = -b / a;
    if (b === 0 || Math.abs(m) > 3 || !Number.isInteger(m * 2)) continue;
    if (sameEq({ kind: 'affine', a, b }, prev)) continue;
    return { a, b };
  }
  return { a: -1, b: 2 };
}

const opt = (segs: Seg[], y?: (x: number) => number, hint?: Seg[]): QuizOption => ({
  segs,
  correct: !hint,
  ...(hint ? { hint } : {}),
  ...(y ? { y } : {}),
});

/** une question du défi ; `prev` évite de reposer la même équation */
export function makeQuiz(kind: QuizKind, rnd: () => number, prev?: Quiz): Quiz {
  if (kind === 'primitive') return makePrimitive(rnd, prev);
  const { a, b } = pickAB(rnd, prev, kind === 'solution-x0' ? [-1, 1] : A_VALUES);
  const m = -b / a;
  const eq: Eq = { kind: 'affine', a, b };
  const mt = num(m);
  if (kind === 'equation') {
    const up = a > 0;
    const e = (aa: number, bb: number, hint?: Seg[]) =>
      opt([eqText({ kind: 'affine', a: aa, b: bb })], undefined, hint);
    return {
      kind,
      eq,
      x0: 0,
      y0: 0,
      options: shuffle(
        [
          e(a, b),
          e(-a, -b, [
            `La droite des segments horizontaux est la bonne (y = ${mt}), mais les pentes sont opposées : au-dessus de cette droite, les segments ${up ? 'montent' : 'descendent'}.`,
          ]),
          e(a, -b, [
            `Les segments sont horizontaux là où ay + b = 0, soit sur la droite y = −b/a = ${mt} ; cette équation la placerait en y = ${num(-m)}.`,
          ]),
          e(-a, b, [
            `Deux erreurs : cette équation a ses segments horizontaux sur y = ${num(-m)} (au lieu de y = ${mt}), et des pentes de sens opposé.`,
          ]),
        ],
        rnd,
      ),
      explain: [
        `Exact : les segments sont horizontaux sur la droite y = −b/a = ${mt} et, au-dessus, ils ${up ? 'montent (a > 0)' : 'descendent (a < 0)'}.`,
      ],
    };
  }
  const sol = (k: number, aa: number, x0: number, mm: number, hint?: Seg[]) =>
    opt(
      [`y = ${coefE(k)}`, { sup: expArg(aa, x0) }, addend(mm)],
      (x) => k * Math.exp(aa * (x - x0)) + mm,
      hint,
    );
  if (kind === 'solution') {
    // y(0) = y₀ entier, différent de m, de −m et de 0 (options toutes distinctes)
    const y0 = pick(
      [-3, -2, -1, 1, 2, 3].filter((v) => v !== m && v !== -m),
      rnd,
    );
    const K = y0 - m;
    return {
      kind,
      eq,
      x0: 0,
      y0,
      options: shuffle(
        [
          sol(K, a, 0, m),
          sol(y0, a, 0, m, [
            'Avec y = Ce',
            { sup: expArg(a, 0) },
            `${addend(m)}, y(0) = C${addend(m)} et non C : il faut C = ${num(y0)} − ${paren(mt)} = ${num(K)}.`,
          ]),
          sol(K, -a, 0, m, [
            `Les solutions de ${eqText(eq)} s’écrivent Ce`,
            { sup: expArg(a, 0) },
            `${addend(m)} : le même a dans l’exposant.`,
          ]),
          sol(y0 + m, a, 0, -m, [
            `La solution constante est m = −b/a = ${mt} (là où y′ = 0) : attention au signe.`,
          ]),
        ],
        rnd,
      ),
      explain: [
        'Exact : les solutions sont y = Ce',
        { sup: expArg(a, 0) },
        `${addend(m)} (m = −b/a = ${mt}) ; la condition initiale y(0) = C${addend(m)} = ${num(y0)} donne C = ${num(K)}.`,
      ],
    };
  }
  // solution-x0 : a = ±1, condition en x₀ = ±1
  const x0 = pick([-1, 1], rnd);
  const y0 = pick(
    [-3, -2, -1, 1, 2, 3].filter((v) => v !== m),
    rnd,
  );
  const K = y0 - m;
  const at = a * x0;
  return {
    kind,
    eq,
    x0,
    y0,
    options: shuffle(
      [
        sol(K, a, x0, m),
        sol(K, a, 0, m, [
          `La condition est en x = ${num(x0)}, pas en 0 : avec cette réponse, y(${num(x0)}) = `,
          ...kExp(K, at),
          `${addend(m)} ≠ ${num(y0)}. Écris la solution sous la forme Ke`,
          { sup: 'a(x − x₀)' },
          ' + m : en x = x₀, l’exponentielle vaut 1.',
        ]),
        sol(K, a, -x0, m, [
          'Attention au signe : c’est e',
          { sup: expArg(a, x0) },
          ` qui vaut 1 en x = ${num(x0)}.`,
        ]),
        sol(y0, a, x0, m, [
          `En x = ${num(x0)}, cette réponse vaut ${num(y0)}${addend(m)} = ${num(y0 + m)} ≠ ${num(y0)} : le coefficient est y₀ − m = ${num(K)}, pas y₀.`,
        ]),
      ],
      rnd,
    ),
    explain: [
      'Exact : y = Ce',
      { sup: expArg(a, 0) },
      `${addend(m)} ; y(${num(x0)}) = C`,
      ...eSegs(at),
      `${addend(m)} = ${num(y0)} donne C = `,
      ...kExp(K, -at),
      ', soit y = ',
      coefE(K),
      { sup: expArg(a, x0) },
      `${addend(m)}.`,
    ],
  };
}

function makePrimitive(rnd: () => number, prev?: Quiz): Quiz {
  let id: PrimId = pick(['lin', 'cubic'] as const, rnd);
  if (prev?.eq.kind === 'prim' && prev.eq.id === id) id = id === 'lin' ? 'cubic' : 'lin';
  const pr = primById(id);
  const eq: Eq = { kind: 'prim', id };
  const x0 = pick(id === 'lin' ? [-2, -1, 1, 2] : [-1, 1, 2], rnd);
  const y0 = pick([-2, -1, 1, 2, 3], rnd);
  const F0 = pr.FExact(x0)!;
  const F0t = exact(F0).plain;
  const Y0 = fromDecimal(y0);
  const c = (C: Frac) => {
    const t = exact(C).plain;
    return C.p === 0 ? '' : C.p > 0 ? ` + ${t}` : ` − ${t.slice(1)}`;
  };
  const prim = (C: Frac, hint?: Seg[]) =>
    opt(['y = ', ...pr.F, c(C)], (x) => pr.Fn(x) + value(C), hint);
  const good = sub(Y0, F0);
  const f0 = fromDecimal(pr.f(x0));
  const Ft = plainText(pr.F);
  return {
    kind: 'primitive',
    eq,
    x0,
    y0,
    options: shuffle(
      [
        prim(good),
        prim(Y0, [
          `y(${num(x0)}) = F(${num(x0)}) + C = ${F0t} + C : il faut C = ${num(y0)} − ${paren(F0t)} = ${exact(good).plain}.`,
        ]),
        prim(sub(Y0, mul(F0, frac(-1))), [
          `C = y₀ − F(x₀), et non y₀ + F(x₀) : ici C = ${num(y0)} − ${paren(F0t)} = ${exact(good).plain}.`,
        ]),
        opt([`y = ${pr.fText}${c(sub(Y0, f0))}`], (x) => pr.f(x) + value(sub(Y0, f0)), [
          `Les solutions de y′ = f sont les primitives de f, F + C, pas f + C : ici F(x) = ${Ft}.`,
        ]),
      ],
      rnd,
    ),
    explain: [
      `Exact : F(x) = ${Ft} est une primitive de f, les solutions sont y = ${Ft} + C, et y(${num(x0)}) = ${F0t} + C = ${num(y0)} donne C = ${exact(good).plain}.`,
    ],
  };
}

/** l'énoncé d'une question */
export function quizQuestion(q: Quiz): string {
  if (q.kind === 'equation')
    return 'Voici le champ de directions d’une équation y′ = ay + b. Laquelle ?';
  return `Quelle est la solution de ${eqText(q.eq)} qui vérifie la condition initiale y(${num(q.x0)}) = ${num(q.y0)} ?`;
}
